import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

import { ENV } from '../../config/env'
import { lang } from '../../localization'

export type AltchaCaptchaRef = {
  reset: () => void
}

// ALTCHA ships no TypeScript types for its custom element, so the ref needs a
// cast. Name the one member actually used rather than reaching for `any`: this
// keeps the escape hatch typed, and makes reset() OPTIONAL at the type level —
// which is precisely the fact the `?.reset?.()` call below depends on.
type AltchaWidgetElement = HTMLElement & { reset?: () => void }

type AltchaCaptchaProps = {
  onVerify: (token: string) => void
  // Called once with whether the widget can actually be solved — i.e. whether
  // the vendored script ran and defined the custom element. Callers use it to
  // decide whether requiring a solved captcha is fair; see the timeout below.
  onAvailabilityChange?: (available: boolean) => void
}

// How long to wait for <altcha-widget> to be defined before treating the captcha
// as unavailable. The script is same-origin and small, so this is generous; it
// exists because customElements.whenDefined() never rejects and never times out
// on its own — if the script 404s or is blocked, the promise simply never
// settles, and a caller waiting on it would wait forever.
const WIDGET_DEFINE_TIMEOUT_MS = 5000

const AltchaCaptcha = forwardRef<AltchaCaptchaRef, AltchaCaptchaProps>(
  ({ onVerify, onAvailabilityChange }, ref) => {
    const widgetRef = useRef<HTMLElement>(null)

    useImperativeHandle(ref, () => ({
      reset: () => {
        // `?.reset?.()`, not `?.reset()`. The first `?.` only guards the ref
        // being null; the element itself exists from first render and gains
        // reset() only once the custom element UPGRADES. If the vendored altcha
        // script has not run — still loading, blocked by an extension or CSP,
        // or 404 from a bad deploy — reset is undefined and calling it throws.
        //
        // That throw lands inside the login mutation's onError handler, so a
        // failed login stops reporting why it failed and surfaces
        // "reset is not a function" instead. The captcha being unavailable must
        // degrade the widget, not the error path around it.
        ;(widgetRef.current as AltchaWidgetElement | null)?.reset?.()
      }
    }))

    useEffect(() => {
      if (!onAvailabilityChange) return
      let settled = false
      const report = (available: boolean) => {
        if (settled) return
        settled = true
        onAvailabilityChange(available)
      }

      const timer = setTimeout(() => report(false), WIDGET_DEFINE_TIMEOUT_MS)
      // Guard the whole call: customElements is absent in non-DOM environments,
      // and an unavailable captcha must never take the login screen down with it.
      try {
        window.customElements
          .whenDefined('altcha-widget')
          .then(() => report(true))
          .catch(() => report(false))
      } catch {
        report(false)
      }

      return () => {
        clearTimeout(timer)
        settled = true
      }
    }, [onAvailabilityChange])

    useEffect(() => {
      const widget = widgetRef.current
      if (!widget) return

      // ALTCHA v3: the solved token arrives on the dedicated `verified` event
      // (`statechange` no longer carries the payload — it only reports `state`).
      const handleVerified = (event: Event) => {
        // Read `detail` before reaching into it. A `verified` event dispatched
        // without one — `new Event('verified')`, or a future ALTCHA revision —
        // would make the destructure throw inside a DOM listener, where the
        // browser swallows it into window.onerror rather than surfacing it to
        // React. The event would be dropped with only a console error to show
        // for it; better to ignore it deliberately.
        const detail = (event as CustomEvent).detail
        if (detail?.payload) onVerify(detail.payload)
      }

      widget.addEventListener('verified', handleVerified)
      return () => widget.removeEventListener('verified', handleVerified)
    }, [onVerify])

    return (
      // v3: `challengeurl` was renamed to `challenge`; `hidelogo`/`hidefooter` moved
      // into the `configuration` JSON (hideLogo/hideFooter).
      <altcha-widget
        ref={widgetRef}
        challenge={`${ENV.GRAPH_BFF}/captcha/challenge`}
        language={lang}
        configuration='{"hideLogo":true,"hideFooter":true}'
        style={{ width: '100%' }}
      />
    )
  }
)

AltchaCaptcha.displayName = 'AltchaCaptcha'

export default AltchaCaptcha
